import { useState, useCallback, useEffect } from 'react';
import { getOrderSummary, calculateCartPrice } from '../api/shop';
import { CartCalculation } from '../types/shop';
import { useTranslation } from 'react-i18next';

interface UsePromoCodesProps {
  cartCalculation: CartCalculation | null;
  onCartUpdate: (newCalculation: CartCalculation) => void;
  isAuthenticated: boolean;
}

export const usePromoCodes = ({ cartCalculation, onCartUpdate, isAuthenticated }: UsePromoCodesProps) => {
  const { t } = useTranslation();
  const [appliedPromoCodes, setAppliedPromoCodes] = useState<string[]>(() => {
    // Initialize with existing applied promo codes from cart calculation
    if (cartCalculation?.validatedDiscounts?.appliedDiscounts) {
      // Filter out automatic discounts - only include manual promo codes
      return cartCalculation.validatedDiscounts.appliedDiscounts
        .filter(discount => !discount.isAutomatic)
        .map(discount => discount.code);
    }
    return [];
  });
  const [newPromoCode, setNewPromoCode] = useState('');
  const [isApplying, setIsApplying] = useState(false);
  const [removingCode, setRemovingCode] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  // Update appliedPromoCodes when cartCalculation changes
  useEffect(() => {
    if (cartCalculation?.validatedDiscounts?.appliedDiscounts) {
      // Filter out automatic discounts - only include manual promo codes
      const codes = cartCalculation.validatedDiscounts.appliedDiscounts
        .filter(discount => !discount.isAutomatic)
        .map(discount => discount.code);
      setAppliedPromoCodes(codes);
    } else {
      setAppliedPromoCodes([]);
    }
  }, [cartCalculation]);

  const getMarketId = useCallback(() => {
    return sessionStorage.getItem('selected_market_id') || '';
  }, []);

  const applyPromoCode = useCallback(async (promoCode: string) => {
    if (!promoCode.trim() || !cartCalculation) {
      return;
    }

    setIsApplying(true);
    setError(null);

    try {
      // Create new promoCodes array with existing + new code
      const updatedPromoCodes = [...appliedPromoCodes, promoCode.trim()];
      
      let result: CartCalculation;
      
      if (isAuthenticated) {
        // For authenticated users, use order-summary API
        result = await getOrderSummary({
          cartItems: [], // Always empty for authenticated users
          shippingMethodId: null,
          marketId: getMarketId(),
          promoCodes: updatedPromoCodes,
        });
      } else {
        // For guest users, use calculate-price API
        const localCart = JSON.parse(sessionStorage.getItem('cart_items') || '[]');
        const items = localCart.map((item: { id: number; quantity: number; variantId?: number; selectedOptions?: { [key: string]: string } }) => ({
          productId: item.id,
          quantity: item.quantity,
          variantId: item.variantId,
          selectedOptions: item.selectedOptions,
        }));
        
        if (!items.length) {
          setError(t('cart.loginToUseDiscountCode'));
          return;
        }
        
        // For guest users, use calculate-price API with all promo codes
        result = await calculateCartPrice({
          cartItems: items,
          shippingMethodId: null,
          marketId: getMarketId(),
          promoCodes: updatedPromoCodes,
        });
      }

      // Check if the new promo code was actually applied
      const newCodeApplied = result.validatedDiscounts?.appliedDiscounts?.some(discount => 
        discount.code === promoCode.trim()
      );

      // Check if the promo code was ignored and get the reason
      const ignoredDiscount = result.validatedDiscounts?.ignoredDiscounts?.find(discount => 
        discount.code === promoCode.trim()
      );

      if (newCodeApplied) {
        // Successfully applied - update cart calculation to sync appliedPromoCodes
        onCartUpdate(result);
        setNewPromoCode('');
        setSuccessMessage(`Promo code has successfully applied!`);
        // Clear success message after 3 seconds
        setTimeout(() => setSuccessMessage(null), 3000);
      } else if (ignoredDiscount) {
        // Promo code was ignored - show the specific reason from ignoredDiscounts
        setError(ignoredDiscount.reason);
      } else {
        // Promo code was not found in ignoredDiscounts either (truly invalid)
        setError(`Invalid promo code: ${promoCode}`);
      }
    } catch (err) {
      console.error('Error applying promo code:', err);
      setError('Failed to apply promo code. Please try again.');
    } finally {
      setIsApplying(false);
    }
  }, [appliedPromoCodes, cartCalculation, onCartUpdate, isAuthenticated, getMarketId, t]);

  const removePromoCode = useCallback(async (promoCodeToRemove: string) => {
    if (!cartCalculation) {
      return;
    }

    // Set removing state for UI feedback
    setRemovingCode(promoCodeToRemove);
    setError(null);

    try {
      // Remove the promo code from the list
      const updatedPromoCodes = appliedPromoCodes.filter(code => code !== promoCodeToRemove);
      
      let result: CartCalculation;
      
      if (isAuthenticated) {
        result = await getOrderSummary({
          cartItems: [],
          shippingMethodId: null,
          marketId: getMarketId(),
          promoCodes: updatedPromoCodes,
        });
      } else {
        const localCart = JSON.parse(sessionStorage.getItem('cart_items') || '[]');
        const items = localCart.map((item: { id: number; quantity: number; variantId?: number; selectedOptions?: { [key: string]: string } }) => ({
          productId: item.id,
          quantity: item.quantity,
          variantId: item.variantId,
          selectedOptions: item.selectedOptions,
        }));
        
        if (!items.length) {
          setError(t('cart.loginToUseDiscountCode'));
          return;
        }
        
        // For guest users, remove the promo code and recalculate
        result = await calculateCartPrice({
          cartItems: items,
          shippingMethodId: null,
          marketId: getMarketId(),
          promoCodes: updatedPromoCodes,
        });
      }

      // Update cart calculation - this will trigger the useEffect to sync appliedPromoCodes
      onCartUpdate(result);
      
      // Check if any previously ignored discounts can now be applied
      if (result.validatedDiscounts?.ignoredDiscounts && result.validatedDiscounts.ignoredDiscounts.length > 0) {
        // Clear any existing error messages since we just removed a code
        setError(null);
      }
    } catch (err) {
      console.error('Error removing promo code:', err);
      setError('Failed to remove promo code. Please try again.');
    } finally {
      setRemovingCode(null);
    }
  }, [appliedPromoCodes, cartCalculation, onCartUpdate, isAuthenticated, getMarketId, t]);

  const clearPromoCodes = useCallback(async () => {
    if (!cartCalculation) {
      return;
    }

    setIsApplying(true);
    setError(null);

    try {
      let result: CartCalculation;
      
      if (isAuthenticated) {
        result = await getOrderSummary({
          cartItems: [],
          shippingMethodId: null,
          marketId: getMarketId(),
          promoCodes: [],
        });
      } else {
        const localCart = JSON.parse(sessionStorage.getItem('cart_items') || '[]');
        const items = localCart.map((item: { id: number; quantity: number; variantId?: number; selectedOptions?: { [key: string]: string } }) => ({
          productId: item.id,
          quantity: item.quantity,
          variantId: item.variantId,
          selectedOptions: item.selectedOptions,
        }));
        
        if (!items.length) {
          setError(t('cart.loginToUseDiscountCode'));
          return;
        }
        
        result = await calculateCartPrice({
          cartItems: items,
          shippingMethodId: null,
          marketId: getMarketId(),
          promoCodes: [],
        });
      }

      // Update cart calculation - this will trigger the useEffect to sync appliedPromoCodes
      onCartUpdate(result);
      
      // Clear any existing error messages when clearing all codes
      setError(null);
    } catch (err) {
      console.error('Error clearing promo codes:', err);
      setError('Failed to clear promo codes. Please try again.');
    } finally {
      setIsApplying(false);
    }
  }, [cartCalculation, onCartUpdate, isAuthenticated, getMarketId, t]);

  return {
    appliedPromoCodes,
    newPromoCode,
    setNewPromoCode,
    isApplying,
    removingCode,
    error,
    setError,
    successMessage,
    setSuccessMessage,
    applyPromoCode,
    removePromoCode,
    clearPromoCodes,
  };
}; 